/* eslint-disable no-console */
import _get from 'lodash/get';
import _kebabCase from 'lodash/kebabCase';
import { Google, Pixall } from '../analytics/index.js';
import { getSortTypeTextHelper, sanitizeValue } from './helpers/index.js';
import { getInventoryOwnerById } from '../utilities/index.js';
import { DataLayerTransformer, filterClickLabelTransformer } from '../transformers/pixall/index.js';
import { inventoryTransformer as googleInventoryTransformer, filterNameTransformer, pageNameTransformer, filterClickEventDataTransformer } from '../transformers/google/index.js';
import { getConsumerMetadata } from '../transformers/pixall/DataLayerTransformer';

/**
 * @summary Sends click events for commercial entry point clicks (promo card and body style filter)
 * @events event140
 */
export async function buyOnlineButton(_ref, option) {
  let {
    data = {},
    dataLayer
  } = _ref;
  const {
    eventSource = 'button',
    filterValues,
    sponsoredMake = ''
  } = data;
  const oem = sponsoredMake ? '::' + sponsoredMake : '';
  const cmp = `cta::oem-buy-online${oem}::filter`;
  const extra = {
    cmp,
    es: eventSource,
    et: 'click'
  };
  Pixall.sendClickEvent({
    eventSource,
    eventType: 'click',
    label: cmp,
    filterValues,
    hasListingType: false
  }, dataLayer, option);
  return extra;
}

/**
 * @summary Sends click events for commercial entry point clicks (promo card and body style filter)
 * @events event134 and 135
 */
export async function commercialEntryPoint(_ref2, option) {
  let {
    data = {},
    dataLayer
  } = _ref2;
  const {
    cmp,
    eventSource,
    filterValues
  } = data;
  const extra = {
    cmp,
    es: eventSource,
    et: 'click'
  };
  Pixall.sendClickEvent({
    eventSource,
    eventType: 'click',
    label: cmp,
    filterValues,
    hasListingType: false
  }, dataLayer, option);
  return extra;
}
/**
 * @summary Sends Google click event for compare button click
 * @events event1
 */
export async function compareListingsClick(_ref3, option) {
  let {
    data = {},
    state,
    dataLayer
  } = _ref3;
  const {
    wasSticky,
    isAddToCompare = false,
    inventoryId,
    pixallData
  } = data;
  const eventResult = 'cta::open::compare';
  if (isAddToCompare) {
    const inventory = getInventoryOwnerById(state, inventoryId);
    const pageId = DataLayerTransformer.getPageId(dataLayer);
    Pixall.sendClickEvent({
      eventSource: 'vehicle',
      eventType: 'click',
      label: 'comp_v',
      listingType: 'alp',
      pageId,
      pageType: 'vdp',
      inventory,
      ...pixallData
    }, dataLayer, option);
  }
  Google.sendClickEvent({
    eventElement: 'compare',
    eventResult
  }, option);
  return {
    cmp: 'compare' + (wasSticky ? '_stky' : '')
  };
}

/**
 * @summary Sends Google click event for turning on compare toggle
 * @events srp event43
 */
export async function compareToggleClick(_ref4, option) {
  let {
    data = {}
  } = _ref4;
  Google.sendClickEvent({
    eventElement: 'compare',
    eventResult: 'toggle::open::compare'
  }, option);
  return {};
}

/**
 * @summary Sends an Adobe link track for changing number of records on SRP
 * @cmp numRecords
 * @events event1
 * @requires data.recordAmount
 */
export async function numRecordsDropDownClick(_ref5, option) {
  let {
    data = {},
    dataLayer
  } = _ref5;
  const {
    recordAmount = 25
  } = data;
  const pageName = _get(dataLayer, 'page.pageName', '');
  Google.sendClickEvent({
    eventElement: pageNameTransformer(pageName) || 'search-car',
    eventResult: `dropdown::show-${recordAmount}`
  }, option);
  return {
    cmp: 'numRecords'
  };
}

/**
 * @summary Sends an Adobe link track for paginating on SRP
 * @cmp pagination
 * @events event1
 * @requires data.pageNumber,
 */
export async function paginationClick(_ref6, option) {
  let {
    data = {},
    state,
    filterDuck,
    paginationDuck,
    resultsDuck,
    userDuck,
    dataLayer
  } = _ref6;
  if (!filterDuck) {
    console.error('filterDuck is missing');
  }
  if (!paginationDuck) {
    console.error('paginationDuck is missing');
  }
  if (!resultsDuck) {
    console.error('resultsDuck is missing');
  }
  if (!userDuck) {
    console.error('userDuck is missing');
  }
  const pagination = paginationDuck ? paginationDuck.selectors.getDuckState(state) : {};
  const searchResults = resultsDuck ? resultsDuck.selectors.getDuckState(state) : {};
  const pageName = _get(dataLayer, 'page.pageName', '');
  const extra = {
    cmp: 'pagination',
    co_txt_url: 'Page ' + data.pageNumber,
    pagination,
    searchResults
  };
  Google.sendClickEvent({
    eventElement: pageNameTransformer(pageName) || 'search-car',
    eventAction: 'paginate',
    // this will be the next page number they are clicking to 'eventValue': '',
    eventResult: `page::${data.pageNumber}`,
    eventData: {}
  }, option);
  Pixall.sendClickEvent({
    eventSource: 'field',
    eventType: 'change',
    label: `page::${data.pageNumber}`,
    hasListingType: false
  }, dataLayer, option);
  return extra;
}

/**
 * @summary Sends a Google/Pixall click event on feature code change
 * @cmp featureCodes
 */
export async function featureCodeClick(_ref7, option) {
  let {
    data = {},
    dataLayer
  } = _ref7;
  const {
    filterName,
    featureCodeName
  } = data;
  const eventValue = featureCodeName.toLowerCase();
  const pageName = _get(dataLayer, 'page.pageName', '');
  Google.sendClickEvent({
    eventElement: pageNameTransformer(pageName) || 'search-car',
    eventAction: 'filter',
    eventResult: `${filterNameTransformer(filterName)}::${sanitizeValue(eventValue)}`
  }, option);
  Pixall.sendClickEvent({
    eventSource: 'field',
    eventType: 'change',
    label: `${filterNameTransformer(filterName)}::${sanitizeValue(eventValue)}`,
    hasListingType: false
  }, dataLayer, option);
  return {};
}

/**
 * @summary Sends a Google click event when clear a filter on Your Search area
 * @event srp event 26
 */

export async function clearChipClick(_temp, option) {
  let {
    data = {}
  } = _temp === void 0 ? {} : _temp;
  const removedValue = _kebabCase(_get(data, 'removedValue', ''));
  Google.sendClickEvent({
    eventElement: 'search-car',
    eventAction: 'filter',
    eventResult: `remove-filter::${removedValue}`
  }, option);
  return {};
}

/**
 * @summary Sends a Google click event when clear all filters
 * @event srp event 27
 */

export async function clearAllFiltersClick(_temp2, option) {
  let {
    data = {}
  } = _temp2 === void 0 ? {} : _temp2;
  const clearFiltersLabel = sanitizeValue(_get(data, 'clearFiltersLabel', ''));
  Google.sendClickEvent({
    eventElement: 'search-car',
    eventAction: 'filter',
    eventResult: `text-link::${clearFiltersLabel}`
  }, option);
  return {};
}

/**
 * @summary Sends Google and Pixall click event for inline SRP Promo click (ex: Loey Logano, etc...)
 * @events event1
 */
export async function promoSrpPlacementClick(metadata, option) {
  if (metadata === void 0) {
    metadata = {};
  }
  const {
    event,
    dataLayer
  } = metadata;
  const textContent = (_get(event, 'target.innerText', '') || _get(event, 'target.textContent', '')).trim();
  const labelText = sanitizeValue(textContent);
  Pixall.sendClickEvent({
    eventSource: 'vehicle',
    feature: 'psx charity',
    label: `cta_${labelText}`,
    listingType: 'pvt'
  }, dataLayer, option);
  Google.sendClickEvent({
    eventElement: 'psx-charity',
    eventAction: 'click',
    eventResult: `cta::${labelText}`
  }, option);
  return {
    cmp: `cta_${labelText}`
  };
}

/**
 * @summary Sends a Google/Pixall click event on search radius and zip value change
 * @cmp searchRadius
 * @events event 96
 */
export async function searchRadiusClick(_ref8, option) {
  let {
    data = {},
    event,
    dataLayer
  } = _ref8;
  const targetValue = event?.target?.value;
  const isZip = data.filterName === 'zip';
  // If new zip is entered: Pass entered zip value, pass the radius for radius, i.e. in GTM: radius::[entered zip]
  // If new search radius is selected: Pass selected radius, pass zip for zip, i.e. in GTM: [selected radius]::zip
  const eventResult = isZip ? `radius::${targetValue}` : `${targetValue}::zip`;
  const pageName = _get(dataLayer, 'page.pageName', '');
  Google.sendClickEvent({
    eventElement: pageNameTransformer(pageName) || 'search-car',
    eventAction: 'filter',
    eventResult,
    eventData: {
      search: {
        [isZip ? 'zipCode' : 'radius']: targetValue
      }
    }
  }, option);
  Pixall.sendClickEvent({
    eventSource: 'field',
    eventType: 'change',
    label: eventResult,
    hasListingType: false
  }, dataLayer, option);
  return {};
}

/**
 * @summary Sends an Google link track for clicking save search link
 * @cmp sv_srch
 * @events event1
 */
export async function saveSearchClick(_ref9, option) {
  let {
    data = {}
  } = _ref9;
  const {
    par,
    isLoggedIn,
    search,
    dataLayer
  } = data;
  const extra = {
    cmp: 'sv_srch',
    par,
    ...data
  };
  Google.sendClickEvent({
    eventElement: 'search-car',
    eventAction: 'save',
    eventResult: `save_${isLoggedIn ? 'logged-in' : 'quick'}::search`,
    eventData: {
      search: search ? googleInventoryTransformer(search, {
        target: 'search',
        dataLayer
      }) : undefined
    }
  }, option);
  return extra;
}

/**
 * @summary Sends an Adobe/Google link track for sorting on SRP
 * @cmp sortBy
 * @events event1
 * @requires data.sortType
 */
export async function sortDropDownClick(_ref10, option) {
  let {
    data = {},
    dataLayer
  } = _ref10;
  const pageName = _get(dataLayer, 'page.pageName', '');
  Google.sendClickEvent({
    eventElement: pageNameTransformer(pageName) || 'search-car',
    eventAction: 'sort',
    eventResult: `sort::${getSortTypeTextHelper(data.sortType)}`
  }, option);
  return {
    cmp: 'sortBy'
  };
}

/**
 * @summary Sends an Adobe link track for clicking suggested filter chip
 * @cmp suggestedFilter
 * @events event1
 */
export async function suggestedFilterClick(_ref11, option) {
  let {
    data = {},
    event
  } = _ref11;
  // Replace numbers, commas, and parentheses with an empty string at the string's end.
  // For example: Toyota(12,345) -> Toyota
  const contentText = _get(event, 'target.innerText', '').trim().replace(/\((\d|,)*\)$/g, '');
  const {
    par
  } = data;
  const extra = {
    cmp: 'suggestedFilter',
    co_txt_url: contentText,
    par,
    ...data
  };
  Google.sendClickEvent({
    eventElement: 'search-car',
    eventAction: 'filter',
    eventResult: `suggested-filter::${sanitizeValue(contentText)}`
  }, option);
  Pixall.sendClickEvent({
    eventSource: 'field',
    eventType: 'change',
    label: `suggested-filter::${sanitizeValue(contentText)}`,
    pageId: 'fyc_srl',
    pageType: 'vlp'
  }, option);
  return extra;
}

/**
 * @summary Sends a Pixall click event clicking a category tab
 * @event srp event 147
 */

export async function searchCategoryClick(_temp3) {
  let {
    data = {}
  } = _temp3 === void 0 ? {} : _temp3;
  const {
    categoryName
  } = data;
  Pixall.sendClickEvent({
    eventSource: 'button',
    eventType: 'click',
    label: `category::${categoryName}`,
    pageId: 'fyc_srl',
    pageType: 'vlp'
  });
  return {};
}

/**
 * @summary Sends a Google click event on filter value change
 * @cmp filter
 * @events event1
 */

export async function filterClick(_ref12, option) {
  let {
    data = {},
    event,
    dataLayer,
    filterDuck,
    resultsDuck,
    state
  } = _ref12;
  const {
    filterName
  } = data;
  const pageName = _get(dataLayer, 'page.pageName', '');

  // Allow for deprecated usage of the direct state lookup in favor of resultsDuck selector
  const deprecatedSelector = _state => _get(_state, `domain.${pageName}.results`, {});
  const statsSelector = resultsDuck ? resultsDuck.selectors.getDuckState : deprecatedSelector;
  const {
    stats: statFilterValue = {}
  } = statsSelector(state);
  const filtersValues = filterDuck ? filterDuck.selectors.getRequestValues(state) : {};
  const eventValue = _get(event, 'target.value', '').toLowerCase();
  const {
    scrubbedEventValue,
    search,
    eventResult
  } = filterClickEventDataTransformer(eventValue, filterName, filtersValues, statFilterValue);
  Google.sendClickEvent({
    eventElement: pageNameTransformer(pageName) || 'search-car',
    eventAction: 'filter',
    eventResult: eventResult || `${filterNameTransformer(filterName.split('|')[0])}::${sanitizeValue(scrubbedEventValue)}`,
    eventData: {
      search
    }
  }, option);
  Pixall.sendClickEvent({
    eventSource: 'field',
    eventType: 'change',
    label: filterClickLabelTransformer(filterName, eventValue),
    pageId: 'fyc_srl',
    pageType: 'vlp',
    hasListingType: false
  }, option);
  return {};
}
export async function sitemapFilterClick(_ref13, option) {
  let {
    data = {}
  } = _ref13;
  const eventData = _get(data, 'eventData', {});
  const sitemapContent = _get(data, 'sitemapContent', '').toLowerCase();
  Google.sendClickEvent({
    eventElement: 'search-dealer',
    eventAction: 'search',
    eventResult: `text-link::srp_dealer::${sitemapContent}`,
    eventData
  }, option);
  return {};
}
export const backToResultsClick = async (_ref14, option) => {
  let {
    dataLayer = {},
    event
  } = _ref14;
  const textContent = (event?.target?.innerText || event?.target?.textContent || '').trim();
  const linkCopy = _kebabCase(textContent);
  const consumerMetadata = await getConsumerMetadata();
  Google.sendClickEvent({
    eventElement: 'search-car',
    eventAction: 'filter',
    eventResult: `text-link::${linkCopy}`,
    eventNonInteractive: true
  }, option);
  Pixall.sendClickEvent({
    eventSource: 'link',
    eventType: 'click',
    label: `text-link::${linkCopy}`,
    pageId: 'fyc_lnd_fba',
    pageType: 'search',
    hasListingType: false,
    consumerMetadata
  }, dataLayer, option);
  return {};
};

/**
 * @summary Handles Buy My Car Online click on promo card fallback
 *
 */

export async function buyOnlinePromoClick(_ref15, option) {
  let {
    data = {},
    dataLayer
  } = _ref15;
  const {
    filterValues,
    sponsoredMake = ''
  } = data;
  let label = 'cta::buy-my-car-online';
  if (sponsoredMake) {
    label = `cta::oem-buy-online-store::${sponsoredMake}::promo-card`;
  }
  await Pixall.sendClickEvent({
    eventSource: 'promotion',
    eventType: 'click',
    label,
    filterValues
  }, dataLayer, option);
  return {
    label
  };
}

/**
 * @summary Handles Buy My Car Online click on promo card impression
 *
 */

export async function buyOnlinePromoImpression(metadata, option) {
  const {
    data
  } = metadata;
  const {
    filterValues
  } = data;
  await Pixall.sendImpression(metadata, {
    eventSource: 'promotion',
    label: 'promo-card-fallback',
    filterValues
  }, option);
  return {};
}

/**
 * @summary Sends click events for view nationwide inventory link
 * @events event141
 */
export async function viewNationwideInventoryClick(_ref16, option) {
  let {
    data = {},
    dataLayer
  } = _ref16;
  const {
    filterValues
  } = data;
  const cmp = 'text-link::view-nationwide-inventory';
  const extra = {
    cmp,
    es: 'link',
    et: 'click'
  };
  Pixall.sendClickEvent({
    eventType: 'click',
    label: cmp,
    eventSource: 'link',
    filterValues,
    hasListingType: false
  }, dataLayer, option);
  return extra;
}

/**
 * @summary Handles Inline Filter on promo card impression
 * @events event 148 - Inline Filter Impression
 */
export async function inlineFilterPromoImpression(metadata, option) {
  await Pixall.sendImpression(metadata, {
    eventSource: 'personalization',
    label: 'inline-filter::suggested-filter'
  }, option);
  return {};
}

/**
 * @summary Handles Inline Filter CTA click on promo card
 * @events event 149 - Inline Filter CTA
 */
export async function inlineFilterPromoClick(_ref17, option) {
  let {
    dataLayer
  } = _ref17;
  const label = 'cta::suggested-inline-filter';
  await Pixall.sendClickEvent({
    eventSource: 'personalization',
    eventType: 'click',
    label
  }, dataLayer, option);
  return {
    label
  };
}
export default {
  buyOnlinePromoClick,
  buyOnlinePromoImpression,
  buyOnlineButton,
  commercialEntryPoint,
  compareListingsClick,
  filterClick,
  numRecordsDropDownClick,
  paginationClick,
  saveSearchClick,
  featureCodeClick,
  clearChipClick,
  clearAllFiltersClick,
  searchRadiusClick,
  sortDropDownClick,
  suggestedFilterClick,
  searchCategoryClick,
  sitemapFilterClick,
  backToResultsClick,
  promoSrpPlacementClick,
  viewNationwideInventoryClick,
  inlineFilterPromoImpression,
  inlineFilterPromoClick
};